'use client';
import { useState, useEffect } from 'react';
import Image from 'next/image';
import { faShare } from '@fortawesome/pro-light-svg-icons';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import { CopyPostToClipboard } from './copy-post-to-clipboard.component';

import { Link } from '@/components/ui/link/link.component';
import { Icon } from '@/components/ui/icon.component';
import { config } from '@/config/config';
import xSvg from '@/assets/svgs/x-logo.svg?url';
import facebookSvg from '@/assets/svgs/facebook-logo.svg?url';
import linkedinSvg from '@/assets/svgs/linkedin-logo.svg?url';
import whatsappSvg from '@/assets/svgs/whatsapp-logo.svg?url';
import telegramSvg from '@/assets/svgs/telegram-logo.svg?url';
import { AnalyticsEnum } from '@/types/enums/analytics/analytics.enum';
import { useNavigation } from '@/hooks/navigation/use-navigation.hook';

interface ShareToSocialProps {
  url: string;
  variant?: 'mobile' | 'desktop';
}

export function ShareToSocial({ url, variant = 'desktop' }: ShareToSocialProps) {
  const baseUrl = `${config.env.baseUrl}/${url}/`;
  const [canUseNativeShare, setCanUseNativeShare] = useState(false);

  useEffect(() => {
    if (variant === 'mobile' && typeof navigator.share === 'function') {
      setCanUseNativeShare(true);
    }
  }, [variant]);

  const handleShare = async (): Promise<void> => {
    await navigator.share({
      title: 'Blog Medprev',
      text: 'Confira esse post do blog da Medprev:',
      url: baseUrl,
    });
  };

  const isMobile = variant === 'mobile';
  const shareText = 'Achei este artigo interessante e resolvi compartilhar com você:';

  if (isMobile && canUseNativeShare) {
    return (
      <div
        className="relative flex items-center justify-end w-full lg:hidden"
        data-testid={DataTestIdEnum.BLOG_SHARE_TO_SOCIAL_MOBILE}
      >
        <button
          onClick={handleShare}
          className="flex items-center justify-center w-8 h-8 rounded-lg text-green-500 transition bg-green-100 hover:bg-green-200"
          data-testid={DataTestIdEnum.NATIVE_SHARE_BUTTON}
        >
          <Icon icon={faShare} className="w-5 h-5" />
        </button>
      </div>
    );
  }

  const shareMessage = `${shareText} ${baseUrl}`;

  const socialLinks = [
    {
      href: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareMessage)}`,
      src: xSvg,
      alt: 'X (Twitter) logo',
      testId: isMobile ? DataTestIdEnum.TWITTER_SHARE : `${DataTestIdEnum.BLOG_SHARE_TO_SOCIAL}-twitter`,
      channel: 'x',
      width: isMobile ? 24 : 32,
      height: isMobile ? 24 : 32,
      className: isMobile ? 'w-5 h-5 transition-transform duration-200 hover:scale-125' : 'w-8 h-8 transition-transform duration-200 hover:scale-125',
    },
    {
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(baseUrl)}&quote=${encodeURIComponent(shareText)}`,
      src: facebookSvg,
      alt: 'Facebook logo',
      testId: isMobile ? DataTestIdEnum.FACEBOOK_SHARE : `${DataTestIdEnum.BLOG_SHARE_TO_SOCIAL}-facebook`,
      channel: 'facebook',
      width: isMobile ? 24 : 32,
      height: isMobile ? 24 : 32,
      className: isMobile ? 'w-5 h-5 transition-transform duration-200 hover:scale-125' : 'w-8 h-8 transition-transform duration-200 hover:scale-125',
    },
    {
      href: `https://www.linkedin.com/shareArticle?url=${encodeURIComponent(baseUrl)}&summary=${encodeURIComponent(shareText)}`,
      src: linkedinSvg,
      alt: 'LinkedIn logo',
      testId: isMobile ? DataTestIdEnum.LINKEDIN_SHARE : `${DataTestIdEnum.BLOG_SHARE_TO_SOCIAL}-linkedin`,
      channel: 'linkedin',
      width: isMobile ? 28 : 40,
      height: isMobile ? 28 : 40,
      className: isMobile ? 'w-6 h-6 transition-transform duration-200 hover:scale-125' : 'w-10 h-10 transition-transform duration-200 hover:scale-125',
    },
    {
      href: `https://api.whatsapp.com/send?text=${encodeURIComponent(shareMessage)}`,
      src: whatsappSvg,
      alt: 'WhatsApp logo',
      testId: isMobile ? DataTestIdEnum.WHATSAPP_SHARE : `${DataTestIdEnum.BLOG_SHARE_TO_SOCIAL}-whatsapp`,
      channel: 'whatsapp',
      width: isMobile ? 24 : 32,
      height: isMobile ? 24 : 32,
      className: isMobile ? 'w-5 h-5 transition-transform duration-200 hover:scale-125' : 'w-8 h-8 transition-transform duration-200 hover:scale-125',
    },
    {
      href: `https://telegram.me/share/url?url=${encodeURIComponent(baseUrl)}&text=${encodeURIComponent(shareText)}`,
      src: telegramSvg,
      alt: 'Telegram logo',
      testId: isMobile ? DataTestIdEnum.TELEGRAM_SHARE : `${DataTestIdEnum.BLOG_SHARE_TO_SOCIAL}-telegram`,
      channel: 'telegram',
      width: isMobile ? 26 : 40,
      height: isMobile ? 26 : 40,
      className: isMobile ? 'w-6 h-6 transition-transform duration-200 hover:scale-125' : 'w-10 h-10 transition-transform duration-200 hover:scale-125',
    },
  ];

  return (
    <div
      className={
        isMobile
          ? 'relative flex items-center justify-end w-full lg:hidden'
          : 'gap-4 w-full items-center justify-start lg:p-2 flex'
      }
      data-testid={
        isMobile
          ? DataTestIdEnum.BLOG_SHARE_TO_SOCIAL_MOBILE
          : DataTestIdEnum.BLOG_SHARE_TO_SOCIAL
      }
    >
      <div
        data-testid={isMobile ? DataTestIdEnum.FALLBACK_SHARE_OPTIONS : undefined}
        className={isMobile ? 'flex gap-2 items-center p-2 rounded-lg shadow-md' : 'contents'}
      >
        {socialLinks.map((social) => (
          <Link
            key={social.channel}
            prefetch={false}
            data-testid={social.testId}
            href={social.href}
            target="_blank"
            rel="noopener noreferrer"
            data-analytics-event={!isMobile ? AnalyticsEnum.SHARE_BLOG_POST : undefined}
            data-analytics-properties={!isMobile ? JSON.stringify({ item_canal: social.channel }) : undefined}
          >
            <Image
              src={social.src}
              width={social.width}
              height={social.height}
              alt={social.alt}
              className={social.className}
            />
          </Link>
        ))}
        <CopyPostToClipboard url={baseUrl} />
      </div>
    </div>
  );
}
